/**
 * Centralized HTTP helpers for sales-order flow (orders, carts, checkout,
 * marketplace/supplier browsing). Import from here instead of inline fetch.
 */

import { getAuthCookie } from "../utils/authCookies";

export const SALES_ORDER_API_BASE = process.env.REACT_APP_API_URL || "";

export type SalesOrderFetchOptions = {
  signal?: AbortSignal;
  /** Return raw Response (caller parses / handles errors). */
  raw?: boolean;
};

type JsonRecord = Record<string, unknown>;

function authHeaders(token: string, json = true): HeadersInit {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

async function parseJsonSafe(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

async function salesOrderFetch(
  path: string,
  token: string,
  init: RequestInit = {},
  options?: SalesOrderFetchOptions
): Promise<Response> {
  const res = await fetch(`${SALES_ORDER_API_BASE}${path}`, {
    ...init,
    signal: options?.signal ?? init.signal,
  });
  if (options?.raw) return res;
  return res;
}

/** Raw Response helper for callers that parse errors / field-level validation themselves. */
export async function salesOrderRequest(
  path: string,
  token: string,
  init: RequestInit = {},
  options?: SalesOrderFetchOptions
): Promise<Response> {
  return salesOrderFetch(path, token, init, { ...options, raw: true });
}

async function salesOrderJson<T = any>(
  path: string,
  token: string,
  init: RequestInit = {},
  options?: SalesOrderFetchOptions
): Promise<T> {
  const res = await salesOrderFetch(path, token, init, options);
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const message =
      (data as { message?: string })?.message ||
      (data as { error?: string })?.error ||
      `Request failed (${res.status})`;
    throw new Error(String(message));
  }
  return data as T;
}

/** Seller organization id for organization-scoped routes (payments, UPS, fees, discounts). */
export function sanitizeOrganizationId(value: unknown): string {
  const id = String(value ?? "").trim();
  if (!id || id === "undefined" || id === "null") return "";
  return id;
}

export function isSignageSellerLicense(): boolean {
  const licenseType = String(getAuthCookie("licenseType") || "").trim().toUpperCase();
  return licenseType === "SIGNAGE_FIRM" || licenseType === "SIGNAGE_TEAM_MEMBER";
}

/** Buyer session org — must never be used as vendor/signage org on brokerage/agent checkout. */
export function getBuyerSessionOrganizationId(): string {
  if (isSignageSellerLicense()) return "";
  return sanitizeOrganizationId(getAuthCookie("organizationId"));
}

function pickVendorOrganizationId(...candidates: unknown[]): string {
  const buyerOrgId = getBuyerSessionOrganizationId();
  for (const candidate of candidates) {
    const id = sanitizeOrganizationId(candidate);
    if (!id) continue;
    if (buyerOrgId && id === buyerOrgId) continue;
    return id;
  }
  return "";
}

export function resolveSupplierOrganizationId(
  supplierId?: string | null,
  order?: { seller_org_id?: string; signage_id?: string } | null
): string {
  const storedSupplierOrg =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("supplierOrganizationId")
      : null;
  const storedSupplierId =
    typeof localStorage !== "undefined" ? localStorage.getItem("supplierId") : null;
  return (
    pickVendorOrganizationId(
      order?.seller_org_id,
      order?.signage_id,
      supplierId,
      storedSupplierOrg,
      storedSupplierId
    ) ||
    // Signage seller may legitimately equal session org; buyers never fall back to session org.
    (isSignageSellerLicense()
      ? sanitizeOrganizationId(order?.seller_org_id || supplierId || storedSupplierOrg || storedSupplierId)
      : "")
  );
}

/** Vendor org for buyer checkout tax/shipping/fees — prefer cart.seller_org_id when loaded. */
export function resolveCheckoutVendorOrganizationId(input?: {
  cartSellerOrgId?: string | null;
  order?: { seller_org_id?: string; signage_id?: string } | null;
  supplierIdFallback?: string | null;
}): string {
  const vendorIds = resolveSelectedVendorFoundationIds(input?.supplierIdFallback);
  return (
    pickVendorOrganizationId(
      input?.cartSellerOrgId,
      input?.order?.seller_org_id,
      input?.order?.signage_id,
      vendorIds.organization_id,
      input?.supplierIdFallback
    ) || resolveSupplierOrganizationId(input?.supplierIdFallback, input?.order)
  );
}

export function persistSupplierOrganizationId(organizationId?: string | null) {
  const id = sanitizeOrganizationId(organizationId);
  if (!id) return;
  // Never persist the buyer session org as the selected vendor/signage org.
  if (getBuyerSessionOrganizationId() && id === getBuyerSessionOrganizationId()) return;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("supplierOrganizationId", id);
  }
}

const SUPPLIER_MEMBERSHIP_KEY = "supplierMembershipId";
const SUPPLIER_GLOBAL_USER_KEY = "supplierGlobalUserId";

/** Selected vendor foundation ids (marketplace seller org / membership / global user). */
export type SelectedVendorFoundationIds = {
  organization_id?: string;
  membership_id?: string;
  global_user_id?: string;
};

export function persistSelectedVendorFoundationIds(
  ids: SelectedVendorFoundationIds
) {
  const organizationId = pickVendorOrganizationId(ids.organization_id);
  if (organizationId) {
    persistSupplierOrganizationId(organizationId);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("supplierId", organizationId);
    }
  }
  const membershipId = String(ids.membership_id || "").trim();
  if (membershipId && typeof localStorage !== "undefined") {
    localStorage.setItem(SUPPLIER_MEMBERSHIP_KEY, membershipId);
  }
  const globalUserId = String(ids.global_user_id || "").trim();
  if (globalUserId && typeof localStorage !== "undefined") {
    localStorage.setItem(SUPPLIER_GLOBAL_USER_KEY, globalUserId);
  }
}

export function resolveSelectedVendorFoundationIds(
  routeSupplierOrgId?: string | null,
  vendor?: SelectedVendorFoundationIds | null
): SelectedVendorFoundationIds {
  const organization_id = String(
    vendor?.organization_id ||
      routeSupplierOrgId ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("supplierOrganizationId")
        : null) ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("supplierId")
        : null) ||
      ""
  ).trim();
  const membership_id = String(
    vendor?.membership_id ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(SUPPLIER_MEMBERSHIP_KEY)
        : null) ||
      ""
  ).trim();
  const global_user_id = String(
    vendor?.global_user_id ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(SUPPLIER_GLOBAL_USER_KEY)
        : null) ||
      ""
  ).trim();
  return { organization_id, membership_id, global_user_id };
}

/** POST /carts body: seller foundation ids from vendor + buyer foundation ids from auth cookies. */
export function buildCartCreateBody(params: {
  cart_items: unknown[];
  supplier_id?: string;
  is_active?: boolean;
  routeSupplierOrgId?: string | null;
  vendor?: SelectedVendorFoundationIds | null;
}) {
  const vendorIds = resolveSelectedVendorFoundationIds(
    params.routeSupplierOrgId,
    params.vendor
  );
  const supplier_id = String(
    params.supplier_id || vendorIds.organization_id || ""
  ).trim();

  const organization_id = String(getAuthCookie("organizationId") || "").trim();
  const membership_id = String(getAuthCookie("membershipId") || "").trim();
  const global_user_id = String(getAuthCookie("globalUserId") || "").trim();

  const body: Record<string, unknown> = {
    cart_items: params.cart_items,
    supplier_id,
    is_active: params.is_active ?? true,
  };

  if (vendorIds.organization_id) body.seller_org_id = vendorIds.organization_id;
  if (vendorIds.membership_id) body.seller_membership_id = vendorIds.membership_id;
  if (vendorIds.global_user_id) body.seller_global_user_id = vendorIds.global_user_id;

  if (organization_id) body.organization_id = organization_id;
  if (membership_id) body.membership_id = membership_id;
  if (global_user_id) body.global_user_id = global_user_id;

  return body;
}

export type CartFoundationIds = {
  sellerOrgId: string;
  sellerMembershipId: string;
  sellerGlobalUserId: string;
  buyerOrgId: string;
  buyerMembershipId: string;
  buyerGlobalUserId: string;
};

/** Read buyer + seller foundation ids from GET /carts/:id response. */
export function readCartFoundationIds(cart: unknown): CartFoundationIds {
  const data = (cart ?? {}) as Record<string, unknown>;
  return {
    sellerOrgId: pickVendorOrganizationId(
      data.seller_org_id,
      data.signage_id,
      data.supplier_id
    ),
    sellerMembershipId: String(data.seller_membership_id ?? "").trim(),
    sellerGlobalUserId: String(data.seller_global_user_id ?? "").trim(),
    buyerOrgId: String(data.organization_id ?? "").trim(),
    buyerMembershipId: String(data.membership_id ?? "").trim(),
    buyerGlobalUserId: String(data.global_user_id ?? "").trim(),
  };
}

/**
 * Seller foundation ids for checkout create/update/resolve.
 * Prefers cart GET values, then saved order, then vendor localStorage/route.
 */
export function resolveCheckoutSellerFoundationIds(input?: {
  cartSellerOrgId?: string | null;
  cartSellerMembershipId?: string | null;
  cartSellerGlobalUserId?: string | null;
  routeSupplierOrgId?: string | null;
  order?: {
    seller_org_id?: string;
    seller_membership_id?: string;
    seller_global_user_id?: string;
  } | null;
}): SelectedVendorFoundationIds {
  const fromOrder = input?.order
    ? {
        organization_id: sanitizeOrganizationId(input.order.seller_org_id),
        membership_id: String(input.order.seller_membership_id ?? "").trim(),
        global_user_id: String(input.order.seller_global_user_id ?? "").trim(),
      }
    : null;
  const fallback = resolveSelectedVendorFoundationIds(
    input?.routeSupplierOrgId,
    fromOrder
  );
  return {
    organization_id:
      sanitizeOrganizationId(input?.cartSellerOrgId) ||
      fromOrder?.organization_id ||
      fallback.organization_id ||
      "",
    membership_id:
      String(input?.cartSellerMembershipId || "").trim() ||
      fromOrder?.membership_id ||
      fallback.membership_id ||
      "",
    global_user_id:
      String(input?.cartSellerGlobalUserId || "").trim() ||
      fromOrder?.global_user_id ||
      fallback.global_user_id ||
      "",
  };
}

/** Vendor marketplace organization id from profile, route param, or localStorage. */
export function resolveVendorOrganizationId(
  profile?: { organization_id?: string } | null,
  routeSupplierId?: string | null
): string {
  const fromProfile = String(profile?.organization_id || "").trim();
  if (fromProfile) return fromProfile;
  const fromRoute = String(routeSupplierId || "").trim();
  if (fromRoute) return fromRoute;
  if (typeof localStorage !== "undefined") {
    return String(
      localStorage.getItem("supplierOrganizationId") ||
        localStorage.getItem("supplierId") ||
        ""
    ).trim();
  }
  return "";
}

/**
 * Signage seller org + buyer membership for checkout fee/tax helpers.
 * - Seller org: supplierOrganizationId / order.seller_org_id / supplierId prop.
 * - Buyer membership: auth cookie for brokerage/agent/teammember; selected person for signage.
 */
export function resolveCheckoutFeeApiIds(
  supplierIdProp?: string | null,
  selectedPersonId?: string | null,
  order?: { seller_org_id?: string; buyer_membership_id?: string; agent_id?: string; team_member_id?: string } | null
): { organizationId: string; membershipId: string } {
  const licenseType = String(getAuthCookie("licenseType") || "").trim().toUpperCase();
  const isSignageSeller =
    licenseType === "SIGNAGE_FIRM" || licenseType === "SIGNAGE_TEAM_MEMBER";

  const organizationId = resolveSellerScopeOrganizationId(supplierIdProp, order);

  const cookieMembershipId = String(getAuthCookie("membershipId") || "").trim();
  const cookieOrgId = String(getAuthCookie("organizationId") || "").trim();

  const membershipId = isSignageSeller
    ? String(
        selectedPersonId ||
          order?.buyer_membership_id ||
          order?.agent_id ||
          order?.team_member_id ||
          ""
      ).trim()
    : cookieMembershipId || cookieOrgId;

  return { organizationId, membershipId };
}

/** Brokerage org id for POST /organizations/:id/offices/select — never membership id. */
export function resolveOfficeSelectBrokerageOrganizationId(
  selection?: {
    organization_id?: string;
    organizationId?: string;
    value?: string;
    license_type?: string;
    licenseType?: string;
  } | null,
  sessionFallback?: string | null
): string {
  const fromSelection = String(
    selection?.organization_id ?? selection?.organizationId ?? ""
  ).trim();
  if (fromSelection) return fromSelection;
  const lt = String(selection?.license_type ?? selection?.licenseType ?? "")
    .trim()
    .toUpperCase();
  // Brokerage firm search picks store org id on `value`.
  if (lt === "BROKERAGE_FIRM") {
    const fromValue = String(selection?.value ?? "").trim();
    if (fromValue && fromValue !== "brokerage-firm") return fromValue;
  }
  return resolveBrokerageOrganizationId(sessionFallback);
}

/** Brokerage org id for office select and org-scoped buyer routes. */
export function resolveBrokerageOrganizationId(
  parentBrokerageOrgId?: string | null
): string {
  return String(
    parentBrokerageOrgId ||
      getAuthCookie("organizationId") ||
      ""
  ).trim();
}

/** Agent / brokerage / teammember logins that read vendor org data during checkout. */
export function isBuyerSideCheckoutLicense(): boolean {
  const licenseType = String(getAuthCookie("licenseType") || "").trim().toUpperCase();
  const userType = String(getAuthCookie("userType") || "").trim().toLowerCase();
  return (
    licenseType === "BROKERAGE_FIRM" ||
    licenseType === "BROKERAGE_TEAM_MEMBER" ||
    licenseType === "BROKERAGE_AGENT" ||
    licenseType === "AGENT" ||
    userType === "realtor" ||
    userType === "agent" ||
    userType === "teammember"
  );
}

/** Always append `?permissions=false` (idempotent). */
export function forcePermissionsFalse(path: string): string {
  if (/[?&]permissions=false\b/.test(path)) return path;
  return path.includes("?") ? `${path}&permissions=false` : `${path}?permissions=false`;
}

/** Normalize list API payloads that may be a bare array or `{ data|results|items: [] }`. */
export function unwrapListResponse(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    for (const key of ["data", "results", "items", "fees", "special_fees"]) {
      if (Array.isArray(o[key])) return o[key] as any[];
    }
  }
  return [];
}

/** Append `?permissions=false` for buyer-side checkout calls into vendor org scope. */
export function appendPermissionsFalse(path: string): string {
  if (!isBuyerSideCheckoutLicense()) return path;
  return forcePermissionsFalse(path);
}

/** Normalize legacy users-search query params for organization-profiles/search. */
export function normalizeOrganizationProfileSearchQuery(queryString: string): string {
  const qs = queryString.startsWith("?") ? queryString.slice(1) : queryString;
  const params = new URLSearchParams(qs);
  const signageFirmID = params.get("signageFirmID");
  if (signageFirmID && !params.get("signageOrganizationID")) {
    params.set("signageOrganizationID", signageFirmID);
  }
  return params.toString();
}

// ─── URL builders (for error logging / legacy patterns) ─────────────────────

export type OrdersSellerEntityType =
  | "agent"
  | "brokerage_agent"
  | "brokerage_team_member"
  | "brokerage_firm"
  | "office";

export const salesOrderUrls = {
  orders: () => `/orders`,
  order: (orderId: string) => `/orders/${orderId}`,
  orderUpdate: (orderId: string, query = "") =>
    `/orders-update/${orderId}${query ? `?${query}` : ""}`,
  orderUpdateTax: (orderId: string) => `/orders-update/${orderId}?update_context=tax`,
  usersSearch: (query = "") => {
    const normalized = normalizeOrganizationProfileSearchQuery(query);
    return `/organization-profiles/search${normalized ? `?${normalized}` : ""}`;
  },
  organizationProfilesSearchDatatable: () => `/organization-profiles/search/datatable`,
  signageAgentsSelect2: (signageId: string, query = "") =>
    `/users/signage/${signageId}/agents/select2${query.startsWith("?") ? query : query ? `?${query}` : ""}`,
  officeSelect: (brokerageOrgId: string) => `/organizations/${brokerageOrgId}/offices/select`,
  /** @deprecated Prefer brokerageTeamMembersByOfficeIdsSelect2 */
  officeMembersSelect2: () => `/offices/members/select2`,
  /** GET — brokerage team members by office(s); query officeIDs[], q, page, limit */
  brokerageTeamMembersByOfficeIdsSelect2: () =>
    `/organization-memberships/brokerage-team-members/by-office-ids/select2`,
  /** GET — brokerage agents by office(s) */
  brokerageAgentsByOfficeIdsSelect2: () =>
    `/organization-memberships/brokerage-agents/by-office-ids/select2`,
  /** GET — offices assigned to brokerage team member membership(s) */
  brokerageTeamMemberOfficesSelect2: () =>
    `/organization-memberships/brokerage-team-members/offices/select2`,
  /** @deprecated POST — use brokerageTeamMembersByOfficeIdsSelect2 */
  officeTeamMembersSelect: (officeId: string) => `/offices/${officeId}/members/select`,
  /** @deprecated POST — use brokerageAgentsByOfficeIdsSelect2 */
  agentSelect: (officeId: string) => `/agent/select/${officeId}`,
  brokerageFirms: () => `/brokerage-firms`,
  officeByBrokerage: (brokerageId: string) => `/office/${brokerageId}`,
  agentsByOffice: (officeId: string) => `/agent/office/${officeId}`,
  customerGroups: () => `/customer-groups?permissions=false`,
  generalSettings: () => `/settings/general?permissions=false`,
  versionOrderNumber: () => `/utils/get-version-order-number`,
  syncUserEmailToDocument: () => `/sync-user-email-to-document`,
  orderStatus: (orderId: string, status: string) => `/orders/${orderId}/${status}`,
  orderPayment: (orderId: string) => `/orders/${orderId}/payment`,
  orderNet30Payment: (orderId: string) => `/orders/${orderId}/net30-payment`,
  ordersSellerEntityDatatable: (entityType: OrdersSellerEntityType, entityId: string) =>
    `/orders/seller/entities/${entityType}/${entityId}/datatable`,
  ordersEntityDatatable: () => `/orders/entities/datatable/`,
  ordersUserDatatable: () => `/orders/user/datatable`,
  ordersOrgDatatable: (orgId: string) => `/orders/organizations/${orgId}/datatable`,
  ordersOrgExcel: (orgId: string) => `/orders/organizations/${orgId}/excel`,
  cart: (cartId: string) => `/carts/${cartId}`,
  cartAddProduct: (cartId: string) => `/carts/${cartId}/add-product`,
  cartRemoveVariant: (cartId: string, productId: string, variantId: string) =>
    `/carts/${cartId}/products/${productId}/variants/${variantId}`,
  // Legacy: /agent/suppliers/:user_id → /agent/suppliers/:membership_id
  agentSuppliers: (membershipId: string) => `/agent/suppliers/${membershipId}`,
  brokerageOfficeSuppliers: () => `/brokerage-office/suppliers`,
  agentLinkSupplier: () => `/agent/link-supplier`,
  officeLinkSupplier: () => `/office/link-supplier`,
  agentUnlinkSupplier: () => `/agent/suppliers/unlink`,
  officeUnlinkSupplier: () => `/office/suppliers/unlink`,
  signageInventory: (signageId: string, productId: string) =>
    `/signage/${signageId}/inventory/${productId}`,
  signageInventoryProducts: (orgId: string) =>
    `/signage/${orgId}/inventory/products`,
  organizationInventoryProductsByBrandsDatatable: (organizationId: string) =>
    `/organizations/${organizationId}/inventory/products-by-brands/datatable`,
  signageProductReviews: (signageId: string, productId: string) =>
    `/signage/${signageId}/products/${productId}/reviews`,
  marketplaceProfile: (organizationId: string, profileId: string) =>
    `/organizations/${encodeURIComponent(organizationId)}/marketplace-profiles/${encodeURIComponent(profileId)}`,
  marketplaceProfilesDatatable: (organizationId: string) =>
    `/organizations/${encodeURIComponent(organizationId)}/marketplace-profiles/datatable`,
  marketplaceReviewsByProfile: (organizationId: string, profileId: string) =>
    `/organizations/${encodeURIComponent(organizationId)}/marketplace-reviews/profile/${encodeURIComponent(profileId)}`,
  marketplaceBrands: (organizationId: string) =>
    `/organizations/${organizationId}/marketplace/brands`,
  marketplaceBrandCategories: (organizationId: string, brandId: string) =>
    `/organizations/${organizationId}/marketplace/brands/${brandId}/categories`,
  membershipProfileAddresses: (membershipId: string) =>
    `/organization-memberships/${encodeURIComponent(membershipId)}/addresses`,
  /** GET /offices/{office_id}/addresses — office-scoped address list */
  officeAddresses: (officeId: string) =>
    `/offices/${encodeURIComponent(officeId)}/addresses`,
  /** @deprecated Use membershipProfileAddresses — path accepted membership_id, not global_user_id. */
  userAddresses: (membershipId: string) =>
    `/organization-memberships/${encodeURIComponent(membershipId)}/addresses`,
  shippingCosts: (organizationId: string) =>
    `/organizations/${encodeURIComponent(organizationId)}/shipping-costs/list`,
  // Legacy: /payments/gateway/created-by/:id → /payments/gateway/organizations/:organization_id
  paymentGateway: (organizationId: string) =>
    `/payments/gateway/organizations/${organizationId}`,
  paymentMethods: (organizationId: string, membershipId: string) =>
    `/payment-methods/organizations/${organizationId}/memberships/${membershipId}`,
  // Legacy: /payments/stripe/payment-intent/:id → /payments/stripe/organizations/:organization_id/payment-intent
  stripePaymentIntent: (organizationId: string) =>
    `/payments/stripe/organizations/${organizationId}/payment-intent`,
  organizationSpecialFees: (organizationId: string) =>
    `/organizations/${organizationId}/special-fees`,
  specialFeesByMembership: (organizationId: string, membershipId: string) =>
    `/special-fees/organizations/${encodeURIComponent(organizationId)}/by-membership/${encodeURIComponent(membershipId)}`,
  specialFeesEligibleForOrder: (orderId: string) =>
    `/special-fees/orders/${encodeURIComponent(orderId)}/eligible`,
  specialFeesCalculateTax: (organizationId: string) =>
    `/special-fees/calculate-tax/organizations/${organizationId}`,
  discountResolve: (organizationId: string) =>
    `/discounts/organizations/${organizationId}/resolve`,
  discountResolveJob: (organizationId: string) =>
    `/discounts/organizations/${organizationId}/resolve-job`,
  upsConfigsByOrganization: (organizationId: string) =>
    `/shipping/ups/organizations/${organizationId}/configs`,
  upsShipperAddressesByOrganization: (organizationId: string) =>
    `/shipping/ups/organizations/${organizationId}/shipper-addresses`,
  upsCalculateTax: (organizationId: string, configId: string) =>
    `/shipping/ups/organizations/${organizationId}/configs/${configId}/calculate-tax`,
  shippingOrdersSearch: (entityId: string) => `/shipping/orders/search/${entityId}`,
  customerGroupsUsersCreatedOrLinked: (organizationId: string) =>
    `/customer-groups/organizations/${organizationId}/users-created-or-linked`,
  newUniqueId: (cartId?: string) =>
    cartId
      ? `/utils/new-unique-id?cart_id=${encodeURIComponent(cartId)}`
      : `/utils/new-unique-id`,
};

// ─── Orders ─────────────────────────────────────────────────────────────────

export async function fetchOrderById(token: string, orderId: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.order(orderId), token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function createOrder(token: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/orders", token, { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) }, options);
}

export async function updateOrder(token: string, orderId: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.orderUpdate(orderId), token, { method: "PATCH", headers: authHeaders(token), body: JSON.stringify(body) }, options);
}

export async function updateOrderTax(token: string, orderId: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.orderUpdateTax(orderId), token, { method: "PUT", headers: authHeaders(token), body: JSON.stringify(body) }, options);
}

export async function patchOrderTax(
  token: string,
  orderId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
): Promise<Response> {
  return salesOrderRequest(salesOrderUrls.orderUpdateTax(orderId), token, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function postOrderAction(
  token: string,
  orderId: string,
  action: string,
  body?: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.orderStatus(orderId, action), token, {
    method: "PUT",
    headers: authHeaders(token),
    body: body != null ? JSON.stringify(body) : undefined,
  }, options);
}

export function resolveOrdersSellerEntityType(
  licenseType?: string | null,
  userType?: string | null
): OrdersSellerEntityType {
  const license = String(licenseType || "").trim().toUpperCase();
  const user = String(userType || "").trim().toLowerCase();

  if (license === "BROKERAGE_AGENT" || user === "brokerage_agent") return "brokerage_agent";
  if (
    license === "BROKERAGE_TEAM_MEMBER" ||
    user === "brokerage_team_member" ||
    user === "team_member"
  ) {
    return "brokerage_team_member";
  }
  if (license === "BROKERAGE_FIRM" || user === "brokerage_firm" || user === "realtor") {
    return "brokerage_firm";
  }
  if (license === "OFFICE" || user === "office") return "office";
  return "agent";
}

export function resolveOrdersSellerEntityId(
  entityType: OrdersSellerEntityType,
  context: {
    membershipId?: string | null;
    organizationId?: string | null;
    officeId?: string | null;
  } = {}
): string {
  if (entityType === "office") {
    return String(context.officeId || "").trim();
  }
  if (entityType === "brokerage_firm") {
    return String(context.membershipId || context.organizationId || "").trim();
  }
  return String(context.membershipId || "").trim();
}

export async function fetchOrdersSellerEntityDatatable(
  token: string,
  entityType: OrdersSellerEntityType,
  entityId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  const id = String(entityId || "").trim();
  if (!id) {
    throw new Error("Orders entity id is missing");
  }
  return salesOrderJson(salesOrderUrls.ordersSellerEntityDatatable(entityType, id), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

/** POST /orders/entities/datatable */
export async function fetchOrdersEntityDatatable(
  token: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.ordersEntityDatatable(), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

/** Realtor user-scoped order list (no entity path segment). */
export async function fetchOrdersUserDatatable(
  token: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.ordersUserDatatable(), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchAllOrdersUser(token: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/orders/user?all=true", token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchOrdersOrganizationDatatable(
  token: string,
  organizationId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.ordersOrgDatatable(organizationId), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function exportOrdersOrganizationExcel(
  token: string,
  organizationId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderFetch(salesOrderUrls.ordersOrgExcel(organizationId), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, { ...options, raw: true });
}

export async function updateAllProductionStatus(
  token: string,
  orderId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(`/orders/${orderId}/update_all_production_status`, token, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function postOrderPayment(token: string, orderId: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.orderPayment(orderId), token, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function postOrderNet30Payment(token: string, orderId: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.orderNet30Payment(orderId), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

// ─── Carts ──────────────────────────────────────────────────────────────────

export async function fetchCartById(token: string, cartId: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.cart(cartId), token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function createCart(token: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/carts", token, { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) }, options);
}

export async function addProductToCart(token: string, cartId: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.cartAddProduct(cartId), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function updateCartItem(
  token: string,
  cartId: string,
  body: unknown,
  method: "PATCH" | "PUT" = "PATCH",
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.cart(cartId), token, {
    method,
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function removeCartProductVariant(
  token: string,
  cartId: string,
  productId: string,
  variantId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.cartRemoveVariant(cartId, productId, variantId), token, {
    method: "DELETE",
    headers: authHeaders(token),
  }, options);
}

export async function patchCart(token: string, cartId: string, body: unknown, options?: SalesOrderFetchOptions) {
  return updateCartItem(token, cartId, body, "PATCH", options);
}

export async function putCart(token: string, cartId: string, body: unknown, options?: SalesOrderFetchOptions) {
  return updateCartItem(token, cartId, body, "PUT", options);
}

export async function deleteCart(token: string, cartId: string, options?: SalesOrderFetchOptions) {
  return salesOrderFetch(salesOrderUrls.cart(cartId), token, {
    method: "DELETE",
    headers: authHeaders(token),
  }, { ...options, raw: true });
}

export async function fetchAgentSuppliers(
  token: string,
  membershipId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.agentSuppliers(membershipId), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchBrokerageOfficeSuppliers(token: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.brokerageOfficeSuppliers(), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function postLinkSupplier(
  token: string,
  userType: "agent" | "realtor",
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  const path =
    userType === "realtor"
      ? salesOrderUrls.officeLinkSupplier()
      : salesOrderUrls.agentLinkSupplier();
  return salesOrderJson(path, token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function postUnlinkSupplier(
  token: string,
  userType: "agent" | "realtor",
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  const path =
    userType === "realtor"
      ? salesOrderUrls.officeUnlinkSupplier()
      : salesOrderUrls.agentUnlinkSupplier();
  return salesOrderJson(path, token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

/** Signage firm / team member — seller org from auth cookie `organizationId`. */
export function resolveSignageSessionOrganizationId(): string {
  return String(getAuthCookie("organizationId") || getAuthCookie("membershipId") || "").trim();
}

/**
 * Seller org for vendor-scoped API routes.
 * Signage login → cookie `organizationId`. Brokerage/agent/teammember → selected vendor org
 * (never the buyer session organizationId).
 */
export function resolveSellerScopeOrganizationId(
  supplierIdFallback?: string | null,
  order?: { seller_org_id?: string; signage_id?: string } | null
): string {
  if (isSignageSellerLicense()) {
    return resolveSignageSessionOrganizationId();
  }
  return resolveSupplierOrganizationId(supplierIdFallback, order);
}

/** @deprecated Use resolveSignageSessionOrganizationId or resolveSellerScopeOrganizationId. */
export function resolveSessionOrganizationId(): string {
  return resolveSellerScopeOrganizationId();
}

// ─── Checkout helpers ───────────────────────────────────────────────────────

/** membership_id for GET /organization-memberships/{membership_id}/addresses */
export function resolveMembershipAddressesRouteId(context: {
  selectedOfficeAgent?: {
    value?: string;
    membershipId?: string;
    membership_id?: string;
  } | null;
  selectedAgent?: {
    value?: string;
    membershipId?: string;
    membership_id?: string;
  } | null;
  selectedTeamMember?: {
    value?: string;
    membershipId?: string;
    membership_id?: string;
  } | null;
  sessionMembershipId?: string | null;
}): string {
  const membershipFrom = (
    opt?: {
      value?: string;
      membershipId?: string;
      membership_id?: string;
    } | null
  ) =>
    String(opt?.membershipId ?? opt?.membership_id ?? opt?.value ?? "").trim();

  if (context.selectedOfficeAgent) {
    const id = membershipFrom(context.selectedOfficeAgent);
    if (id) return id;
  }
  if (context.selectedAgent) {
    const id = membershipFrom(context.selectedAgent);
    if (id) return id;
  }
  if (context.selectedTeamMember) {
    const id = membershipFrom(context.selectedTeamMember);
    if (id) return id;
  }
  return String(
    context.sessionMembershipId ?? getAuthCookie("membershipId") ?? ""
  ).trim();
}

/**
 * @deprecated Use resolveMembershipAddressesRouteId — old route used office/global ids.
 */
export function resolveUserAddressesRouteId(
  buyerUserType: string,
  selection?: {
    value?: string;
    id?: string;
    _id?: string;
    membershipId?: string;
    membership_id?: string;
    organizationId?: string;
    organization_id?: string;
  } | null,
  options?: { officeId?: string | null }
): string {
  const type = String(buyerUserType || "").trim().toLowerCase();
  if (type === "agent") {
    return membershipFromSelection(selection);
  }
  if (type === "teammember" || type === "office") {
    return String(
      selection?.membershipId ??
        selection?.membership_id ??
        selection?.value ??
        selection?.id ??
        selection?._id ??
        ""
    ).trim();
  }
  return membershipFromSelection(selection);
}

function membershipFromSelection(
  selection?: {
    value?: string;
    id?: string;
    _id?: string;
    membershipId?: string;
    membership_id?: string;
  } | null
): string {
  return String(
    selection?.membershipId ??
      selection?.membership_id ??
      selection?.value ??
      selection?.id ??
      selection?._id ??
      ""
  ).trim();
}

/** Brokerage org id for organization-profiles/search?licenseType=BROKERAGE_AGENT&brokerageFirmID= */
export function resolveBrokerageFirmIdForAgentSearch(
  sessionOrgId?: string | null
): string {
  return resolveBrokerageOrganizationId(sessionOrgId);
}

/** @deprecated Prefer resolveUserAddressesRouteId for checkout buyer-type rules. */
export function resolveAddressOwnerGlobalUserId(
  user?: {
    addressOwnerId?: string;
    globalUserId?: string;
    global_user_id?: string;
  } | null,
  cart?: { global_user_id?: string } | null,
  sessionFallback?: string | null
): string {
  return String(
    user?.addressOwnerId ||
      user?.globalUserId ||
      user?.global_user_id ||
      cart?.global_user_id ||
      sessionFallback ||
      getAuthCookie("globalUserId") ||
      ""
  ).trim();
}

export async function fetchUserAddresses(
  token: string,
  membershipId: string,
  options?: SalesOrderFetchOptions
) {
  const id = String(membershipId || "").trim();
  if (!id) throw new Error("membership_id is required");
  return salesOrderJson(salesOrderUrls.membershipProfileAddresses(id), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

/** GET /offices/{office_id}/addresses */
export async function fetchOfficeAddresses(
  token: string,
  officeId: string,
  options?: SalesOrderFetchOptions
) {
  const id = String(officeId || "").trim();
  if (!id) throw new Error("office_id is required");
  return salesOrderJson(salesOrderUrls.officeAddresses(id), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

/** Normalize office/membership address list payload to an array. */
export function unwrapAddressListResponse(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    for (const key of ["data", "results", "items", "addresses", "officeAddress", "office_address"]) {
      const v = o[key];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

/** GET /organization-memberships/{membership_id}/addresses */
export async function fetchMembershipProfileAddresses(
  token: string,
  membershipId: string,
  options?: SalesOrderFetchOptions
) {
  return fetchUserAddresses(token, membershipId, options);
}

export type MembershipProfileAddressCreateBody = {
  label?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  address_details: Record<string, unknown>;
};

/** POST /organization-memberships/{membership_id}/addresses */
export async function createMembershipProfileAddress(
  token: string,
  membershipId: string,
  body: MembershipProfileAddressCreateBody,
  options?: SalesOrderFetchOptions
): Promise<{ message?: string; address_id: string }> {
  const id = String(membershipId || "").trim();
  if (!id) throw new Error("membership_id is required");
  return salesOrderJson(salesOrderUrls.membershipProfileAddresses(id), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchUserById(token: string, userId: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(`/users/${userId}`, token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function searchUsers(token: string, queryString: string, options?: SalesOrderFetchOptions) {
  const path = salesOrderUrls.usersSearch(queryString);
  return salesOrderJson(path, token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function searchUsersDatatable(token: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.organizationProfilesSearchDatatable(), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchSignageAgentsSelect2(
  token: string,
  signageId: string,
  queryString: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(
    `/users/signage/${signageId}/agents/select2${queryString.startsWith("?") ? queryString : `?${queryString}`}`,
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchOfficeSelect(
  token: string,
  brokerageOrgId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(appendPermissionsFalse(salesOrderUrls.officeSelect(brokerageOrgId)), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export type Select2PageOptions = {
  page?: number;
  limit?: number;
  search?: string;
  q?: string;
};

function buildSelect2OfficeIdsQuery(
  officeIds: string[],
  options?: Select2PageOptions
): string {
  const params = new URLSearchParams({
    page: String(options?.page ?? 1),
    limit: String(options?.limit ?? 10),
  });
  const q = String(options?.q ?? options?.search ?? "").trim();
  if (q) params.set("q", q);
  officeIds.filter(Boolean).forEach((id) => {
    params.append("office_ids", id);
    params.append("officeIDs[]", id);
  });
  return params.toString();
}

/** Normalize organization-memberships select2 rows (snake_case + foundation ids). */
export function normalizeSelect2MembershipRow(
  row: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!row || typeof row !== "object") return {};
  const firstName = String(row.first_name ?? row.firstName ?? "").trim();
  const lastName = String(row.last_name ?? row.lastName ?? "").trim();
  const name =
    String(row.name ?? row.text ?? "").trim() ||
    `${firstName} ${lastName}`.trim();
  const membershipId = String(
    row.membership_id ?? row.membershipId ?? row.id ?? row._id ?? ""
  ).trim();
  return {
    ...row,
    id: membershipId || row.id || row._id,
    membership_id: membershipId,
    membershipId,
    global_user_id: row.global_user_id ?? row.globalUserId,
    globalUserId: row.global_user_id ?? row.globalUserId,
    organization_id: row.organization_id ?? row.organizationId,
    organizationId: row.organization_id ?? row.organizationId,
    first_name: firstName,
    last_name: lastName,
    firstName,
    lastName,
    name,
    phone1: row.phone1 ?? row.phone ?? "",
    phone2: row.phone2 ?? "",
    local_license_type:
      row.local_license_type ?? row.licenseType ?? row.license_type,
  };
}

function unwrapSelect2Payload(data: unknown): {
  results: Record<string, unknown>[];
  pagination?: { more?: boolean };
} {
  const root = (data as { data?: unknown })?.data ?? data;
  const row = root as { results?: unknown[]; pagination?: { more?: boolean } };
  const raw = Array.isArray(row?.results) ? row.results : [];
  return {
    results: raw.map((r) =>
      normalizeSelect2MembershipRow(r as Record<string, unknown>)
    ),
    pagination: row?.pagination,
  };
}

/** GET /organization-memberships/brokerage-team-members/by-office-ids/select2 */
export async function fetchOfficeTeamMembersSelect2(
  token: string,
  officeIds: string | string[],
  options?: Select2PageOptions & SalesOrderFetchOptions
) {
  const ids = (Array.isArray(officeIds) ? officeIds : [officeIds]).filter(Boolean);
  const query = buildSelect2OfficeIdsQuery(ids, options);
  const data = await salesOrderJson(
    `${salesOrderUrls.brokerageTeamMembersByOfficeIdsSelect2()}?${query}`,
    token,
    { method: "GET", headers: authHeaders(token, false) },
    options
  );
  return unwrapSelect2Payload(data);
}

/** @deprecated Use fetchOfficeTeamMembersSelect2 */
export async function fetchOfficeTeamMembersSelect(
  token: string,
  officeId: string,
  body: { page?: number; limit?: number; search?: string },
  options?: SalesOrderFetchOptions
) {
  return fetchOfficeTeamMembersSelect2(token, officeId, {
    page: body?.page,
    limit: body?.limit,
    search: body?.search,
    ...options,
  });
}

/** GET /organization-memberships/brokerage-agents/by-office-ids/select2 */
export async function fetchBrokerageAgentsByOfficeIdsSelect2(
  token: string,
  officeIds: string | string[],
  options?: Select2PageOptions & SalesOrderFetchOptions
) {
  const ids = (Array.isArray(officeIds) ? officeIds : [officeIds]).filter(Boolean);
  const query = buildSelect2OfficeIdsQuery(ids, options);
  const data = await salesOrderJson(
    `${salesOrderUrls.brokerageAgentsByOfficeIdsSelect2()}?${query}`,
    token,
    { method: "GET", headers: authHeaders(token, false) },
    options
  );
  return unwrapSelect2Payload(data);
}

/** @deprecated Use fetchBrokerageAgentsByOfficeIdsSelect2 */
export async function fetchAgentSelect(
  token: string,
  officeId: string,
  body: { page?: number; limit?: number; search?: string },
  options?: SalesOrderFetchOptions
) {
  return fetchBrokerageAgentsByOfficeIdsSelect2(token, officeId, {
    page: body?.page,
    limit: body?.limit,
    search: body?.search,
    ...options,
  });
}

/** GET /organization-memberships/brokerage-team-members/offices/select2 */
export async function fetchBrokerageTeamMemberOfficesSelect2(
  token: string,
  membershipIds: string | string[],
  options?: Select2PageOptions & SalesOrderFetchOptions
) {
  const ids = (Array.isArray(membershipIds) ? membershipIds : [membershipIds]).filter(Boolean);
  const params = new URLSearchParams({
    page: String(options?.page ?? 1),
    limit: String(options?.limit ?? 10),
  });
  const q = String(options?.q ?? options?.search ?? "").trim();
  if (q) params.set("q", q);
  ids.forEach((id) => params.append("membershipIDs[]", id));
  const data = await salesOrderJson(
    `${salesOrderUrls.brokerageTeamMemberOfficesSelect2()}?${params.toString()}`,
    token,
    { method: "GET", headers: authHeaders(token, false) },
    options
  );
  return unwrapSelect2Payload(data);
}

export async function fetchOfficeById(token: string, officeId: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(`/offices/${officeId}`, token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchBrokerageFirms(token: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/brokerage-firms", token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchOfficeByBrokerageId(token: string, brokerageId: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(`/office/${brokerageId}`, token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchAgentsByOfficeId(token: string, officeId: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(`/agent/office/${officeId}`, token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchCustomerGroups(token: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/customer-groups?permissions=false", token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchGeneralSettings(token: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/settings/general?permissions=false", token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchNewUniqueId(token: string, cartId?: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson(salesOrderUrls.newUniqueId(cartId), token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function fetchShippingCosts(
  token: string,
  organizationId?: string | null,
  options?: SalesOrderFetchOptions
) {
  const orgId = String(organizationId ?? resolveSellerScopeOrganizationId()).trim();
  if (!orgId) {
    throw new Error("Shipping costs organization id is required");
  }
  return salesOrderJson(forcePermissionsFalse(salesOrderUrls.shippingCosts(orgId)), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchPaymentGatewayBySupplier(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(appendPermissionsFalse(salesOrderUrls.paymentGateway(organizationId)), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchSpecialFeesByMembership(
  token: string,
  organizationId: string,
  membershipId: string,
  options?: SalesOrderFetchOptions
) {
  const orgId = String(organizationId || "").trim();
  const memberId = String(membershipId || "").trim();
  if (!orgId || !memberId) {
    throw new Error("organization_id and membership_id are required for special fees");
  }
  return salesOrderJson(
    forcePermissionsFalse(salesOrderUrls.specialFeesByMembership(orgId, memberId)),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchSpecialFeesEligibleForOrder(
  token: string,
  orderId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(
    forcePermissionsFalse(salesOrderUrls.specialFeesEligibleForOrder(orderId)),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchUpsConfigsByOrganization(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(
    forcePermissionsFalse(salesOrderUrls.upsConfigsByOrganization(organizationId)),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchUpsShipperAddressesByOrganization(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(
    forcePermissionsFalse(salesOrderUrls.upsShipperAddressesByOrganization(organizationId)),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchActiveTaxType(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
) {
  const orgId = sanitizeOrganizationId(organizationId);
  if (!orgId) throw new Error("organization_id is required for active tax type");
  return salesOrderJson(
    forcePermissionsFalse(`/organizations/${orgId}/tax/active-type`),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchPaymentMethodsBySupplierAndUser(
  token: string,
  organizationId: string,
  membershipId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.paymentMethods(organizationId, membershipId), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

/**
 * Buyer membership id for GET /payment-methods/organizations/{org}/memberships/{membership_id}.
 * Prefers foundation membership fields; falls back to legacy agent/team member ids.
 */
/** Seller / vendor membership id from a saved sales order. */
export function resolveSalesOrderSellerMembershipId(
  order?: {
    seller_membership_id?: string;
  } | null
): string {
  return String(order?.seller_membership_id ?? "").trim();
}

/** Team member membership id from a saved sales order (foundation + legacy). */
export function resolveSalesOrderTeamMemberMembershipId(
  order?: {
    team_member_membership_id?: string;
    team_member_id?: string;
  } | null
): string {
  return String(
    order?.team_member_membership_id ?? order?.team_member_id ?? ""
  ).trim();
}

/** Brokerage firm organization id from a saved sales order (foundation + legacy). */
export function resolveSalesOrderBrokerageFirmOrgId(
  order?: {
    brokerage_firm_org_id?: string;
    brokerage_firm_id?: string;
  } | null
): string {
  return String(
    order?.brokerage_firm_org_id ?? order?.brokerage_firm_id ?? ""
  ).trim();
}

/** Agent / end-user membership id from a saved sales order (foundation + legacy). */
export function resolveSalesOrderAgentMembershipId(
  order?: {
    agent_membership_id?: string;
    agent_id?: string;
    agent_name?: string;
    customer_membership_id?: string;
    customer_id?: string;
  } | null
): string {
  const fromAgent = String(
    order?.agent_membership_id ?? order?.agent_id ?? ""
  ).trim();
  if (fromAgent) return fromAgent;
  // Agent end-user orders may only stamp agent_name + customer_* memberships.
  if (String(order?.agent_name ?? "").trim()) {
    return String(
      order?.customer_membership_id ?? order?.customer_id ?? ""
    ).trim();
  }
  return "";
}

const SALES_ZERO_OID = "000000000000000000000000";
const SALES_ZERO_DATE_PREFIX = "0001-01-01";

export function isSalesZeroObjectId(value: unknown): boolean {
  const s = String(value ?? "").trim();
  return !s || s === SALES_ZERO_OID;
}

function isSalesZeroDate(value: unknown): boolean {
  const s = String(value ?? "").trim();
  return !s || s.startsWith(SALES_ZERO_DATE_PREFIX);
}

function omitZeroObjectIdField<T extends Record<string, unknown>>(
  obj: T,
  key: keyof T
): void {
  if (isSalesZeroObjectId(obj[key])) {
    delete obj[key];
  }
}

function sanitizeActorStamp(
  actor: unknown
): { id?: string; name?: string } | undefined {
  if (!actor || typeof actor !== "object") return undefined;
  const raw = actor as { id?: unknown; name?: unknown };
  const id = isSalesZeroObjectId(raw.id) ? "" : String(raw.id ?? "").trim();
  const name = String(raw.name ?? "").trim();
  if (!id && !name) return undefined;
  return {
    ...(id ? { id } : {}),
    ...(name ? { name } : {}),
  };
}

/**
 * Drop Go zero ObjectIDs / zero dates / empty actor stamps so POST /orders
 * does not send 000000000000000000000000 or 0001-01-01T00:00:00Z as real values.
 */
export function sanitizeSalesOrderAddressForPayload(address: unknown): Record<string, unknown> | null {
  if (!address || typeof address !== "object") return null;
  const src = address as Record<string, unknown>;
  const detailsSrc =
    src.address_details && typeof src.address_details === "object"
      ? ({ ...(src.address_details as Record<string, unknown>) } as Record<string, unknown>)
      : null;

  const line1 = String(detailsSrc?.addressLine1 ?? "").trim();
  const rootId = String(src._id ?? "").trim();
  const detailId = String(detailsSrc?._id ?? "").trim();
  const hasUsableId =
    (!isSalesZeroObjectId(rootId) && !!rootId) ||
    (!isSalesZeroObjectId(detailId) && !!detailId);
  if (!hasUsableId && !line1) return null;

  const out: Record<string, unknown> = { ...src };
  delete out.value;

  omitZeroObjectIdField(out, "_id");
  omitZeroObjectIdField(out, "user_id");
  omitZeroObjectIdField(out, "owner_node_id");

  if (!String(out.user_type ?? "").trim()) delete out.user_type;
  if (!String(out.owner_node_type ?? "").trim()) delete out.owner_node_type;

  if (isSalesZeroDate(out.created_at)) delete out.created_at;
  if (isSalesZeroDate(out.updated_at)) delete out.updated_at;

  const createdBy = sanitizeActorStamp(out.created_by);
  if (createdBy) out.created_by = createdBy;
  else delete out.created_by;

  const updatedBy = sanitizeActorStamp(out.updated_by);
  if (updatedBy) out.updated_by = updatedBy;
  else delete out.updated_by;

  if (detailsSrc) {
    omitZeroObjectIdField(detailsSrc, "_id");
    out.address_details = detailsSrc;
  }

  return out;
}

export function findSalesOrderAddressById(
  addresses: Array<{ _id?: string; address_details?: { _id?: string } }> | null | undefined,
  id?: string | null
): any | null {
  const needle = String(id ?? "").trim();
  if (!needle || needle === SALES_ZERO_OID) return null;
  return (
    (addresses || []).find(
      (a) =>
        resolveSalesOrderAddressId(a) === needle ||
        String(a?._id ?? "") === needle ||
        String(a?.address_details?._id ?? "") === needle
    ) || null
  );
}

/** True when saved order address has a real id and line (not API placeholder zeros). */
export function isSalesOrderSavedAddress(
  address?: {
    _id?: string;
    address_details?: { _id?: string; addressLine1?: string };
  } | null
): boolean {
  if (!address || typeof address !== "object") return false;
  const rootId = String(address._id ?? "").trim();
  const detailId = String(address.address_details?._id ?? "").trim();
  const line1 = String(address.address_details?.addressLine1 ?? "").trim();
  const hasRoot = Boolean(rootId && rootId !== SALES_ZERO_OID);
  const hasDetail = Boolean(detailId && detailId !== SALES_ZERO_OID && line1);
  return hasRoot || hasDetail;
}

/** Resolve react-select / form value id for a saved sales order address row. */
export function resolveSalesOrderAddressId(
  address?: { _id?: string; address_details?: { _id?: string } } | null
): string {
  if (!address) return "";
  const rootId = String(address._id ?? "").trim();
  if (rootId && rootId !== SALES_ZERO_OID) return rootId;
  const detailId = String(address.address_details?._id ?? "").trim();
  if (detailId && detailId !== SALES_ZERO_OID) return detailId;
  return "";
}

export function mergeSalesOrderSavedAddresses<
  T extends { _id?: string; address_details?: { _id?: string } },
>(addressObjects: T[], ...savedAddresses: Array<T | null | undefined>): T[] {
  let result = addressObjects;
  for (const raw of savedAddresses) {
    if (!isSalesOrderSavedAddress(raw)) continue;
    const id = resolveSalesOrderAddressId(raw);
    if (!id) continue;
    const normalized = {
      ...raw,
      _id: String(raw?._id ?? id),
    } as T;
    const exists = result.some(
      (addr) =>
        String(addr._id ?? "") === id ||
        String(addr.address_details?._id ?? "") === id
    );
    if (!exists) result = [normalized, ...result];
  }
  return result;
}

export function buildSalesAddressSelectOptions(
  addressObjects: any[] | null | undefined,
  savedAddresses?: Array<any | null | undefined>
) {
  const merged = savedAddresses?.length
    ? mergeSalesOrderSavedAddresses(addressObjects ?? [], ...savedAddresses)
    : (addressObjects ?? []);
  return merged.map((address) => {
    const d = address?.address_details ?? {};
    const value = resolveSalesOrderAddressId(address) || address._id;
    return {
      ...address,
      value,
      label: `${address?.label ?? ""}: ${d.addressLine1 ?? ""}, ${d.city ?? ""}, ${d.state ?? ""}, ${d.zip ?? ""}`,
    };
  });
}

export function resolvePaymentMethodsMembershipId(
  order?: {
    inventory_source?: string;
    buyer_membership_id?: string;
    customer_membership_id?: string;
    agent_membership_id?: string;
    team_member_membership_id?: string;
    agent_id?: string;
    team_member_id?: string;
    customer_id?: string;
    source_membership_id?: string;
  } | null,
  sessionMembershipIdFallback?: string | null
): string {
  const sessionMembershipId = String(
    sessionMembershipIdFallback ?? getAuthCookie("membershipId") ?? ""
  ).trim();
  if (!order) return sessionMembershipId;

  const preferred = String(
    order.buyer_membership_id ??
      order.customer_membership_id ??
      order.agent_membership_id ??
      order.team_member_membership_id ??
      order.source_membership_id ??
      ""
  ).trim();
  if (preferred) return preferred;

  const isOffice = String(order.inventory_source || "").trim().toLowerCase() === "office";
  const legacy = isOffice
    ? String(order.team_member_id ?? order.customer_id ?? "").trim()
    : String(order.agent_id ?? order.customer_id ?? "").trim();
  if (legacy) return legacy;

  return sessionMembershipId;
}

export async function createStripePaymentIntent(
  token: string,
  organizationId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  const amountCents = Number((body as { amount?: unknown } | null)?.amount ?? 0);
  if (!(amountCents > 0)) {
    // Stripe rejects amount 0 — skip the network call for fully paid / $0 balances.
    return { skipped: true, clientSecret: null, client_secret: null };
  }
  return salesOrderJson(salesOrderUrls.stripePaymentIntent(organizationId), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function uploadDocumentsToOrder(token: string, body: FormData, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/upload-documents-to-order", token, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body,
  }, options);
}

export async function fetchAddresses(token: string, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/addresses", token, { method: "GET", headers: authHeaders(token) }, options);
}

export async function createAddress(token: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/addresses", token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchVersionOrderNumber(token: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/utils/get-version-order-number", token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

const ZERO_OBJECT_ID = "000000000000000000000000";

/** First non-empty, non-zero Mongo id. Used for POST /sync-user-email-to-document. */
export function pickSyncMembershipId(...values: unknown[]): string {
  for (const value of values) {
    const id = String(value ?? "").trim();
    if (id && id !== ZERO_OBJECT_ID) return id;
  }
  return "";
}

/** @deprecated Use pickSyncMembershipId */
export const pickSyncGlobalUserId = pickSyncMembershipId;

export type SyncUserEmailToDocumentBody = {
  membership_id: string;
  id: string;
  collection_name: string;
};

export async function syncUserEmailToDocument(
  token: string,
  body: SyncUserEmailToDocumentBody,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson("/sync-user-email-to-document", token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      membership_id: String(body?.membership_id || "").trim(),
      id: String(body?.id || "").trim(),
      collection_name: String(body?.collection_name || "").trim(),
    }),
  }, options);
}

// ─── Marketplace / supplier / products ──────────────────────────────────────

export async function fetchSignageInventoryProduct(
  token: string,
  signageId: string,
  productId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.signageInventory(signageId, productId), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchSignageInventoryProducts(
  token: string,
  organizationId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.signageInventoryProducts(organizationId), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchSignageInventoryProductsList(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
): Promise<Response> {
  return salesOrderRequest(salesOrderUrls.signageInventoryProducts(organizationId), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export type OrganizationInventoryProductsByBrandsDatatableBody = {
  page: number;
  limit: number;
  search?: string;
  filters?: JsonRecord;
  sort?: { field?: string; order?: string };
};

export async function fetchOrganizationInventoryProductsByBrandsDatatable(
  token: string,
  organizationId: string,
  body: OrganizationInventoryProductsByBrandsDatatableBody,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(
    salesOrderUrls.organizationInventoryProductsByBrandsDatatable(organizationId),
    token,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(body),
    },
    options
  );
}

export async function fetchSignageMarketplaceProductsDatatable(
  token: string,
  organizationId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(`/organizations/${organizationId}/marketplace/products/datatable`, token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchMarketplaceProfile(
  token: string,
  organizationId: string,
  profileId: string,
  options?: SalesOrderFetchOptions
) {
  const orgId = String(organizationId || "").trim();
  const id = String(profileId || "").trim();
  if (!orgId) throw new Error("organization_id is required");
  if (!id) throw new Error("profile_id is required");
  return salesOrderJson(salesOrderUrls.marketplaceProfile(orgId, id), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchMarketplaceProfilesByOrganization(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(`/marketplace-profiles/organizations/${organizationId}`, token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

/** @deprecated Use fetchMarketplaceProfilesByOrganization — param is organization_id */
export async function fetchMarketplaceProfileByCreatedBy(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
) {
  return fetchMarketplaceProfilesByOrganization(token, organizationId, options);
}

export async function fetchMarketplaceProfilesDatatable(
  token: string,
  organizationId: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  const orgId = String(organizationId || "").trim();
  if (!orgId) {
    throw new Error("organization_id is required");
  }
  return salesOrderJson(salesOrderUrls.marketplaceProfilesDatatable(orgId), token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchMarketplaceReviewsByProfile(
  token: string,
  organizationId: string,
  profileId: string,
  options?: SalesOrderFetchOptions
) {
  const orgId = String(organizationId || "").trim();
  const id = String(profileId || "").trim();
  if (!orgId) throw new Error("organization_id is required");
  if (!id) throw new Error("profile_id is required");
  return salesOrderJson(salesOrderUrls.marketplaceReviewsByProfile(orgId, id), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchSignageMarketplaceBrands(
  token: string,
  organizationId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.marketplaceBrands(organizationId), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchSignageMarketplaceBrandCategories(
  token: string,
  organizationId: string,
  brandId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.marketplaceBrandCategories(organizationId, brandId), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function fetchCategoriesByMembership(
  token: string,
  membershipId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(`/categories/created-by-membership/${membershipId}`, token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

/** @deprecated Use fetchCategoriesByMembership — param is membership_id */
export async function fetchCategoriesByCreatedBy(
  token: string,
  membershipId: string,
  options?: SalesOrderFetchOptions
) {
  return fetchCategoriesByMembership(token, membershipId, options);
}

export async function fetchProductReviewsSummary(
  token: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson("/product-reviews/summary", token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export async function fetchSignageProductReviews(
  token: string,
  signageId: string,
  productId: string,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson(salesOrderUrls.signageProductReviews(signageId, productId), token, {
    method: "GET",
    headers: authHeaders(token),
  }, options);
}

export async function createProductReview(token: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/product-reviews", token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

export type PublicDiscountCodesByOrganizationsBody = {
  organization_ids: string[];
};

export async function fetchPublicDiscountCodesByOrganizations(
  token: string,
  body: PublicDiscountCodesByOrganizationsBody,
  options?: SalesOrderFetchOptions
) {
  return salesOrderJson("/discounts/public-codes/by-organizations", token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}

/** @deprecated Use fetchPublicDiscountCodesByOrganizations */
export async function fetchPublicDiscountCodesByUsers(
  token: string,
  body: unknown,
  options?: SalesOrderFetchOptions
) {
  const legacy = body as { user_ids?: string[]; organization_ids?: string[] };
  const organizationIds = legacy.organization_ids?.length
    ? legacy.organization_ids
    : legacy.user_ids || [];
  return fetchPublicDiscountCodesByOrganizations(token, { organization_ids: organizationIds }, options);
}

export async function postVendorRequest(token: string, body: unknown, options?: SalesOrderFetchOptions) {
  return salesOrderJson("/vendors/requests", token, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  }, options);
}
